import damerau from 'damerau-levenshtein';
import { get } from 'lodash';
import { FuzzySearchResult, SliceElement } from './Search.interface';

export const splitWords = (term: string): string[] => term.split(/\s{1,}/);

/* We want to make sure T is always a complex type with properties to search in
  eslint-disable @typescript-eslint/ban-types */
export const getLowestDistanceForProps = <T extends object>(
  searchTerm: string,
  entry: T,
  searchProperties: string[],
): FuzzySearchResult<T> | null => {
  let lowest: FuzzySearchResult<T> | null = null;

  for (
    let index = 0, length_ = searchProperties.length;
    index < length_;
    ++index
  ) {
    let propertyValue = get(entry, searchProperties[index], '');

    if (propertyValue === null) {
      continue;
    }

    propertyValue = propertyValue as string;
    const distance = damerau(propertyValue, searchTerm).steps;

    if (lowest === null || lowest.distance > distance) {
      lowest = {
        distance,
        record: entry,
        result: propertyValue,
      };
    }
  }

  return lowest as FuzzySearchResult<T>;
};

export const isExactMatch = <T extends object>(
  searchTerm: string,
  entry: T,
  searchProperties: string[],
  doSplitWords: boolean = true,
): boolean => {
  const searchTermLc = searchTerm.toLowerCase();
  const searchWords = doSplitWords ? splitWords(searchTermLc) : [searchTerm];

  return searchWords.length === 1
    ? isExactMatchKeyword(searchTermLc, entry, searchProperties)
    : isExactMatchPhrase(searchWords, entry, searchProperties);
};

export const isExactMatchKeyword = <T extends object>(
  searchTerm: string,
  entry: T,
  searchProperties: string[],
  useContains: boolean = true,
): boolean => {
  for (const property of searchProperties) {
    let propertyValue = get(entry, property);

    if (propertyValue === null) {
      return false;
    }

    propertyValue = String(propertyValue).toLowerCase();

    if (
      propertyValue.length > searchTerm.length &&
      propertyValue.startsWith(searchTerm)
    ) {
      return true;
    }

    if (
      propertyValue.length === searchTerm.length &&
      propertyValue === searchTerm
    ) {
      return true;
    }

    if (
      useContains &&
      propertyValue.length > searchTerm.length &&
      propertyValue.includes(searchTerm)
    ) {
      return true;
    }
  }

  return false;
};

export const isExactMatchPhrase = <T extends object>(
  words: string[],
  entry: T,
  searchProperties: string[],
): boolean => {
  let count = 0;

  for (const word of words) {
    if (isExactMatchKeyword(word, entry, searchProperties, false)) {
      ++count;
    }
  }

  return count === words.length;
};

/**
 * Ordered array with fixed length. Inserts are sorted immediately ascending
 * by weight. Elements that would be inserted after the last element,
 * if max size is already reached, get discarded.
 */
export class FixedSlice<T> {
  elements: SliceElement<T>[] = [];
  size: number = 0;

  /**
   * @param maxSize Max elements
   */
  constructor(maxSize: number) {
    this.size = maxSize;
  }

  add(weight: number, value: T): void {
    if (this.elements.length === 0) {
      this.elements.push({
        weight,
        value,
      });
      return;
    }

    for (
      let index = 0;
      index < this.size - 1 && index < this.elements.length;
      ++index
    ) {
      if (this.elements[index].weight > weight) {
        this.elements.splice(index, 0, {
          value,
          weight,
        });

        if (this.elements.length > this.size) {
          this.elements.splice(
            this.elements.length - 1,
            this.elements.length - this.size,
          );
        }

        return;
      }
    }

    if (this.elements.length < this.size) {
      this.elements.push({ weight, value });
    }
  }

  values(): T[] {
    return this.elements.map(element => element.value);
  }
}
/* eslint-enable @typescript-eslint/ban-types */
